import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { FileText, HandCoins, Loader2, MessageCircle, Ticket, UserPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button, buttonVariants } from "../../../components/ui/button";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "../../../components/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip";
import { cn } from "../../../handlers/cn.utils";
import { clampPage, pageCount, pageRange, pageWindow, sliceForPage } from "../../../handlers/pagination";
import {
	CLAIMABLE_COMPLETION_PCT,
	completionPctOf,
	describeActivity,
	describeNextStep,
	emailOnReward,
	filterRewards,
	isCollectable,
	NO_REWARD_FILTERS,
	type Reward,
	type RewardFilters,
	referralReminderMessage,
	STATUS_LABELS,
	titleCaseName,
	whatsappLink,
} from "../../../handlers/rewards";
import { currencyFormat } from "../../../handlers/utilityFunctions";
import ActivityFilters from "./ActivityFilters";

// Extended here rather than relied on from whichever screen happened to load first.
dayjs.extend(relativeTime);

const COLUMNS = ["Activity", "Initiated", "Status", "Points", "Action"];

/** Rewards are few enough that a taller page is mostly one page, and paging is the rarer path. */
const ROWS_PER_PAGE = 10;

interface ActivityListProps {
	rewards: Reward[];
	/** Named on the reader's own resume row, which the server does not always carry an address on. */
	accountEmail?: string;
	onCollect: (reward: Reward) => void;
	onRemind: (reward: Reward) => void;
	onRedeem: () => void;
	/** The rewards currently being collected — more than one when the wallet collects them together. */
	collectingIds: string[];
}

function StampCell({ value }: { value?: string | null }) {
	if (!value || !dayjs(value).isValid()) return <span className="text-sm text-gray-400">&mdash;</span>;

	return (
		<div className="font-inter tabular-nums">
			<p className="text-sm text-gray-700">{dayjs(value).format("D MMM YYYY")}</p>
			<p className="text-xs text-gray-400">{dayjs(value).format("HH:mm")}</p>
		</div>
	);
}

/**
 * Status, and the date it reached that status.
 *
 * The sentence per row this replaces explained why a reward was locked. That reason now lives on
 * hover, where it costs the table no width.
 */
function StatusCell({ reward }: { reward: Reward }) {
	const tone =
		reward.rewardStatus === "CLAIMED"
			? "bg-green-100 text-green-700"
			: reward.rewardStatus === "REDEEMED"
				? "bg-gray-100 text-gray-600"
				: "bg-amber-100 text-amber-700";

	const movedOn = reward.rewardStatus === "REDEEMED" ? reward.redeemedDate : reward.claimedDate;

	return (
		<div title={describeNextStep(reward)}>
			<span className={cn("inline-block rounded-full px-2.5 py-0.5 text-xs font-medium font-inter", tone)}>
				{STATUS_LABELS[reward.rewardStatus]}
			</span>
			{movedOn && dayjs(movedOn).isValid() && (
				<p className="mt-1 text-xs text-gray-400 tabular-nums font-inter">{dayjs(movedOn).format("D MMM YYYY")}</p>
			)}
		</div>
	);
}

const RING_RADIUS = 18;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * Who this row is about, ringed by how far their resume has come.
 *
 * The ring runs to the threshold that releases the points, not to 100%, so a full ring means
 * collectable rather than a finished resume. A collected reward reports no percentage of its own —
 * the server stops tracking one once it pays out — so it draws full rather than from that gap.
 *
 * Hovering the ring trades the icon for the number it stands for. The icon says which kind of row
 * this is, which the name beside it says too, so the number is the better use of that space for as
 * long as someone is looking at it.
 */
function ProgressRing({ reward }: { reward: Reward }) {
	const tracked = reward.activityType !== "JOB_LEAD";
	const pending = reward.rewardStatus === "PENDING";
	const pct = completionPctOf(reward);
	const ratio = !tracked ? 0 : pending ? Math.min(1, pct / CLAIMABLE_COMPLETION_PCT) : 1;
	const measured = tracked && pending;

	return (
		<div
			className="group relative size-11 shrink-0"
			role={measured ? "progressbar" : undefined}
			aria-valuenow={measured ? pct : undefined}
			aria-valuemin={measured ? 0 : undefined}
			aria-valuemax={measured ? CLAIMABLE_COMPLETION_PCT : undefined}
			aria-label={
				measured
					? reward.activityType === "REFERRAL"
						? `${describeActivity(reward)} resume completion`
						: "Your resume completion"
					: undefined
			}
		>
			<svg className="absolute inset-0 size-full -rotate-90" viewBox="0 0 40 40" aria-hidden="true">
				<circle cx="20" cy="20" r={RING_RADIUS} fill="none" strokeWidth="3" className="stroke-gray-100" />
				<circle
					cx="20"
					cy="20"
					r={RING_RADIUS}
					fill="none"
					strokeWidth="3"
					strokeLinecap="round"
					strokeDasharray={RING_LENGTH}
					strokeDashoffset={RING_LENGTH * (1 - ratio)}
					className={cn(
						"transition-[stroke-dashoffset] duration-700 ease-out motion-reduce:transition-none",
						ratio >= 1 ? "stroke-green-600" : "stroke-primary-700",
					)}
				/>
			</svg>
			<span
				className={cn(
					"absolute inset-0 flex items-center justify-center leading-none text-gray-500 transition-opacity duration-150 motion-reduce:transition-none",
					measured && "group-hover:opacity-0",
				)}
			>
				{reward.activityType === "REFERRAL" ? <UserPlus className="size-4" /> : <FileText className="size-4" />}
			</span>
			{measured && (
				<span className="absolute inset-0 flex items-center justify-center text-xs font-semibold leading-none tabular-nums text-gray-900 opacity-0 transition-opacity duration-150 group-hover:opacity-100 motion-reduce:transition-none font-inter">
					{pct}%
				</span>
			)}
		</div>
	);
}

function ActivityCell({ reward, accountEmail }: { reward: Reward; accountEmail?: string }) {
	const email = emailOnReward(reward, accountEmail);

	return (
		<div className="flex items-center gap-3">
			<ProgressRing reward={reward} />

			<div className="w-[180px]">
				<p className="truncate text-sm font-medium text-gray-900 font-inter">{describeActivity(reward)}</p>
				{email && <p className="truncate text-xs text-gray-400 font-inter">{email}</p>}
			</div>
		</div>
	);
}

/**
 * An icon button that says what it does on hover and to a screen reader.
 *
 * The label is carried by the tooltip and `aria-label` rather than visible text, so a row of actions
 * stays narrow enough for the table without the labels wrapping.
 */
function IconAction({
	label,
	ariaLabel = label,
	icon,
	onClick,
	href,
	disabled,
	variant = "outlineSecondary",
}: {
	label: string;
	/** Falls back to the tooltip, and overrides it where that is too terse to identify the row. */
	ariaLabel?: string;
	icon: React.ReactNode;
	onClick: () => void;
	/** Set when the action leaves the app, so it opens in a new tab from a real link. */
	href?: string;
	disabled?: boolean;
	variant?: "default" | "outlineSecondary";
}) {
	const button = href ? (
		// Styled directly rather than through `<Button asChild>`. That nests two Slots, and `Button` is
		// a plain function component, so on React 18 the trigger's ref never reaches a DOM node and the
		// tooltip has nothing to anchor to — it simply never opens.
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			onClick={onClick}
			aria-label={ariaLabel}
			className={cn(buttonVariants({ variant, size: "icon" }), "size-9")}
		>
			{icon}
		</a>
	) : (
		<Button
			size="icon"
			variant={variant}
			className="size-9"
			onClick={onClick}
			disabled={disabled}
			aria-label={ariaLabel}
		>
			{icon}
		</Button>
	);

	return (
		// The provider's 700ms default reads as no tooltip at all on a row someone is scanning.
		<Tooltip delayDuration={0}>
			{/* A disabled button fires no pointer events, so the tooltip that explains *why* it is
			    disabled would never open. The span keeps the trigger hoverable and focusable. */}
			<TooltipTrigger asChild>
				{disabled ? (
					<span tabIndex={0} className="inline-flex">
						{button}
					</span>
				) : (
					button
				)}
			</TooltipTrigger>
			<TooltipContent variant="plain" className="font-inter text-xs">
				{label}
			</TooltipContent>
		</Tooltip>
	);
}

interface RowHandlers {
	onCollect: (reward: Reward) => void;
	onRemind: (reward: Reward) => void;
	onRedeem: () => void;
}

interface RowAction {
	/** What the button says where there is room for words. */
	short: string;
	/** The tooltip on a desktop row, and the `title` on a card. */
	hint: string;
	/** The accessible name, where the tooltip is too terse to tell one row's button from another's. */
	ariaLabel?: string;
	icon: React.ReactNode;
	disabled: boolean;
	primary: boolean;
	run: () => void;
	/** Present when the action hands off to another app rather than calling the API. */
	href?: string;
}

/**
 * Everything this row can do, in the order it is worth doing.
 *
 * Collect stays on the row while it is locked rather than appearing the moment it works, so the row
 * reads the same shape throughout and the reason it cannot be pressed is on the button itself. The
 * server is still the authority — it answers 409 below the threshold — so this only ever hides an
 * action the API would refuse. Once the points are collected the same slot becomes Redeem, since
 * that is the only thing left to do with them. Both the table row and the card read from this, so the
 * two surfaces cannot come to different conclusions about what a reward offers.
 */
function rowActions(reward: Reward, busy: boolean, handlers: RowHandlers): RowAction[] {
	const actions: RowAction[] = [];

	if (reward.rewardStatus === "PENDING") {
		const ready = isCollectable(reward);
		actions.push({
			short: "Collect",
			hint: ready ? `Collect ${currencyFormat(reward.rewardPoints)} points` : describeNextStep(reward),
			icon: busy ? <Loader2 className="size-4 animate-spin" /> : <HandCoins className="size-4" />,
			disabled: busy || !ready,
			primary: ready,
			run: () => handlers.onCollect(reward),
		});
	}

	// Collected points are only worth anything at checkout, so give them the way there.
	if (reward.rewardStatus === "CLAIMED") {
		actions.push({
			short: "Redeem",
			hint: "Redeem",
			icon: <Ticket className="size-4" />,
			disabled: false,
			primary: false,
			run: handlers.onRedeem,
		});
	}

	if (reward.activityType === "REFERRAL" && reward.rewardStatus === "PENDING") {
		// A message the referrer sends themselves, rather than the server's email. The email is
		// throttled to one a week and lands in the same inbox that already ignored the invitation;
		// this opens WhatsApp with the text ready and can be sent whenever they think to.
		const name = titleCaseName(reward.referredFullName).split(" ")[0] || "your friend";
		actions.push({
			short: "Remind",
			hint: "Reminder",
			// Every referral row carries this button, so the tooltip alone would read as a list of
			// identical "Reminder"s to anyone tabbing through them.
			ariaLabel: `Remind ${name} on WhatsApp`,
			icon: <MessageCircle className="size-4" />,
			disabled: false,
			primary: false,
			run: () => handlers.onRemind(reward),
			href: whatsappLink(referralReminderMessage(reward), reward.referredPhoneNumber),
		});
	}

	return actions;
}

function ActionCell({ reward, busy, handlers }: { reward: Reward; busy: boolean; handlers: RowHandlers }) {
	const actions = rowActions(reward, busy, handlers);
	if (actions.length === 0) return <span className="text-sm text-gray-400">&mdash;</span>;

	return (
		<div className="flex items-center justify-end gap-2">
			{actions.map((action) => (
				<IconAction
					key={action.short}
					label={action.hint}
					ariaLabel={action.ariaLabel}
					icon={action.icon}
					onClick={action.run}
					href={action.href}
					disabled={action.disabled}
					variant={action.primary ? "default" : "outlineSecondary"}
				/>
			))}
		</div>
	);
}

/** The same action as a labelled button, for the card where there is room for the words. */
function CardAction({ action }: { action: RowAction }) {
	const variant = action.primary ? "default" : "outlineSecondary";
	const body = (
		<>
			{action.icon}
			<span className="ml-1.5">{action.short}</span>
		</>
	);

	if (action.href) {
		return (
			<Button variant={variant} className="w-full" asChild>
				<a href={action.href} target="_blank" rel="noopener noreferrer" onClick={action.run} title={action.hint}>
					{body}
				</a>
			</Button>
		);
	}

	return (
		<Button variant={variant} onClick={action.run} disabled={action.disabled} title={action.hint} className="w-full">
			{body}
		</Button>
	);
}

/** When a reward started, in the terms of the thing that started it. */
function describeInitiated(reward: Reward): string | null {
	if (!reward.initiatedDate || !dayjs(reward.initiatedDate).isValid()) return null;

	const when = dayjs(reward.initiatedDate).fromNow();
	if (reward.activityType === "REFERRAL") return `Invited ${when}`;
	if (reward.activityType === "JOB_LEAD") return `Shared ${when}`;
	return `Started ${when}`;
}

/**
 * The same row, for a screen too narrow to hold a table.
 *
 * A phone gets the four things a decision needs — who, how far along, what it is worth, and what to
 * do — stacked rather than a table dragged sideways. The exact timestamp goes, since a card has no
 * column headings to make a bare date and time mean anything.
 */
function ActivityCard({
	reward,
	accountEmail,
	busy,
	handlers,
}: {
	reward: Reward;
	accountEmail?: string;
	busy: boolean;
	handlers: RowHandlers;
}) {
	const actions = rowActions(reward, busy, handlers);
	const initiated = describeInitiated(reward);
	const email = emailOnReward(reward, accountEmail);

	return (
		<article className="border-b border-gray-100 p-4 last:border-b-0">
			<div className="flex items-start gap-3">
				<ProgressRing reward={reward} />

				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium text-gray-900 font-inter">{describeActivity(reward)}</p>
					{email && <p className="truncate text-xs text-gray-400 font-inter">{email}</p>}
					{initiated && <p className="mt-0.5 text-xs text-gray-500 font-inter">{initiated}</p>}
					<p className="mt-1">
						<span
							className={cn(
								"text-base font-bold tabular-nums font-plus-jakarta",
								reward.rewardStatus === "CLAIMED" ? "text-green-700" : "text-gray-900",
							)}
						>
							{currencyFormat(reward.rewardPoints)}
						</span>
						<span className="ml-1 text-xs text-gray-400 font-inter">pts</span>
					</p>
				</div>

				<div className="shrink-0 text-right">
					<StatusCell reward={reward} />
				</div>
			</div>

			{actions.length > 0 && (
				<div className={cn("mt-3 grid gap-2", actions.length > 1 && "grid-cols-2")}>
					{actions.map((action) => (
						<CardAction key={action.short} action={action} />
					))}
				</div>
			)}
		</article>
	);
}

/**
 * Every reward, as a table where there is room and as cards where there is not.
 *
 * The page this replaces also had two layouts, but each read the data for itself and the two drifted
 * apart. Here both render the same cells and ask `nextAction` the same question, so a change to what
 * a reward offers reaches both. It is the working surface rather than the summary: the wallet above
 * skims the balance, and everything that needs a decision happens here.
 */
function ActivityList({ rewards, accountEmail, onCollect, onRemind, onRedeem, collectingIds }: ActivityListProps) {
	const [filters, setFilters] = useState<RewardFilters>(NO_REWARD_FILTERS);
	const [page, setPage] = useState(1);
	const visible = useMemo(() => filterRewards(rewards, filters), [rewards, filters]);
	const handlers = { onCollect, onRemind, onRedeem };

	const totalPages = pageCount(visible.length, ROWS_PER_PAGE);
	// Narrowing the list can strand the reader past the end of it — collecting the last reward on
	// page 3 of 3 leaves 3 pages' worth of nothing.
	useEffect(() => {
		setPage((current) => clampPage(current, visible.length, ROWS_PER_PAGE));
	}, [visible.length]);

	const rows = sliceForPage(visible, page, ROWS_PER_PAGE);
	const range = pageRange(page, visible.length, ROWS_PER_PAGE);

	return (
		<section>
			{rewards.length > 0 && (
				<ActivityFilters
					filters={filters}
					onChange={(next) => {
						setFilters(next);
						setPage(1);
					}}
				/>
			)}

			<div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
				{rewards.length === 0 ? (
					<p className="px-6 py-10 text-center text-sm text-gray-500 font-inter">
						Nothing here yet. Finish a resume or invite a friend to start earning.
					</p>
				) : visible.length === 0 ? (
					<div className="px-6 py-10 text-center">
						<p className="text-sm text-gray-500 font-inter">No rewards match these filters.</p>
						<button
							type="button"
							onClick={() => setFilters(NO_REWARD_FILTERS)}
							className="mt-2 text-sm font-medium text-primary-700 hover:underline font-inter"
						>
							Clear filters
						</button>
					</div>
				) : (
					<>
						<div className="md:hidden">
							{rows.map((reward) => (
								<ActivityCard
									key={reward._id}
									reward={reward}
									accountEmail={accountEmail}
									handlers={handlers}
									busy={collectingIds.includes(reward._id)}
								/>
							))}
						</div>

						<div className="hidden md:block">
							<Table className="min-w-[640px]">
								<TableHeader>
									<TableRow className="border-gray-100 hover:bg-transparent">
										{COLUMNS.map((column, index) => (
											<TableHead
												key={column}
												className={cn(
													"py-3 text-xs font-medium uppercase tracking-wide text-gray-500 font-inter",
													index === 0 ? "pl-6 pr-3" : "px-3",
													index === COLUMNS.length - 1 && "pl-3 pr-6 text-right",
													column === "Points" && "text-right",
												)}
											>
												{column}
											</TableHead>
										))}
									</TableRow>
								</TableHeader>

								<TableBody>
									{rows.map((reward) => (
										<TableRow key={reward._id} className="border-gray-100 hover:bg-gray-50">
											<TableCell className="py-4 pl-6 pr-3">
												<ActivityCell reward={reward} accountEmail={accountEmail} />
											</TableCell>

											<TableCell className="px-3 py-4">
												<StampCell value={reward.initiatedDate} />
											</TableCell>

											<TableCell className="px-3 py-4">
												<StatusCell reward={reward} />
											</TableCell>

											<TableCell
												className={cn(
													"px-3 py-4 text-right text-base font-bold tabular-nums font-plus-jakarta",
													reward.rewardStatus === "CLAIMED" ? "text-green-700" : "text-gray-900",
												)}
											>
												{currencyFormat(reward.rewardPoints)}
											</TableCell>

											<TableCell className="py-4 pl-3 pr-6 text-right">
												<ActionCell reward={reward} handlers={handlers} busy={collectingIds.includes(reward._id)} />
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>

						{/* The count stays visible on one page so the table always says how much it is showing;
						    the controls only appear once they have somewhere to go. */}
						<div className="flex flex-col items-center gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row sm:justify-between sm:px-6">
							<p className="text-xs text-gray-500 tabular-nums font-inter">
								Showing {range.from}&ndash;{range.to} of {visible.length}
							</p>

							{totalPages > 1 && (
								<Pagination className="mx-0 w-auto justify-end">
									<PaginationContent>
										<PaginationItem>
											<PaginationPrevious
												onClick={() => setPage((current) => Math.max(1, current - 1))}
												disabled={page === 1}
											/>
										</PaginationItem>

										{pageWindow(page, totalPages).map((slot, index) => (
											<PaginationItem key={slot === "gap" ? `gap-${index}` : slot}>
												{slot === "gap" ? (
													<PaginationEllipsis />
												) : (
													<PaginationLink isActive={slot === page} onClick={() => setPage(slot)}>
														{slot}
													</PaginationLink>
												)}
											</PaginationItem>
										))}

										<PaginationItem>
											<PaginationNext
												onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
												disabled={page === totalPages}
											/>
										</PaginationItem>
									</PaginationContent>
								</Pagination>
							)}
						</div>
					</>
				)}
			</div>
		</section>
	);
}

export default ActivityList;
